import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Ad, AppContent, ErrorBoundary, MMPlayerPlaceHolder } from 'mm-ui-components';
import { Store } from 'redux';
import { Theme } from '../../components/connectedComponents/Theme';
import { PostMetaTags } from '../../components/connectedComponents/PostMetaTags';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import {
  getAuthors,
  getBody,
  getBy,
  getCover,
  getCreatedAt,
  getCreatedAtISO,
  getFiniteScrollArticles,
  getImageCover,
  getBreadCrumbs,
  getMetadataDescription,
  getNoIndex,
  getOgDescription,
  getOgImage,
  getOgTitle,
  getOutbrainWidgetID,
  getOutbrainFiniteScrollWidgetID,
  getSeoDescription,
  getSeoTitle,
  getShareConfig,
  getShowRecommendationsAdvertisement,
  getSlideshowData,
  getSponsor,
  getTags,
  getTitle,
  getTwitterDescription,
  getTwitterImage,
  getTwitterTitle,
  getUpdatedAt,
  getUpdatedAtISO,
  getShowUpdatedAt,
  getDisclaimerText,
  getShouldDisplayDisclaimerAtBottom,
  getShouldAddMMPlayerPlaceholder,
  getShouldSetMinHeightForTopAd,
  getCommercialTags,
  getFiniteScrollArticlesURLs,
  getLoadingArticles,
  getFaqData,
  getArticleId,
  getIntro,
  getShouldShowHeroImage,
  getOwnerUsername,
} from '../../store/template/postPage/postPageBali/postPageBali.selectors';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getSiteName,
  getTemplateName,
  getExperiments,
  getResourceID,
  getIsMobileViewer,
} from '../../store/config/config.selectors';
import { SLOT_NAMES } from '../templates.const';

import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { BaliArticleComponent } from './BaliArticleComponent';
import { FiniteScroll } from '../../components/finiteScroll/FiniteScroll';
import { PostPageDataType } from '../../store/template/postPage/types';
import { getCookieByName } from '../templates.utils';

interface PostPageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

export const PostPageBaliComponent: React.FunctionComponent<PostPageProps> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <PostPageBaliContentConnected />
      </ErrorBoundary>
    </Page>
  );
};

export const PostPageBaliContentConnected: React.FunctionComponent = () => {
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const oneOnOneId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2Id = useSelector(getAdSlotId(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomId = useSelector(getAdSlotId(SLOT_NAMES.STICKY_BOTTOM));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const oneOnOneOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const sideBar1AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_1));
  const sideBar2AdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.SIDE_BAR_2));
  const stickyBottomOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.STICKY_BOTTOM));
  const title = useSelector(getTitle);
  const intro = useSelector(getIntro);
  const by = useSelector(getBy);
  const authors = useSelector(getAuthors);
  const cover = useSelector(getCover);
  const body = useSelector(getBody);
  const shareConfig = useSelector(getShareConfig);
  const canonical = useSelector(getCanonicalUrl);
  const templateName = useSelector(getTemplateName);
  const siteName = useSelector(getSiteName);
  const sponsor = useSelector(getSponsor);
  const showRecommendationsAdvertisement = useSelector(getShowRecommendationsAdvertisement);
  const slideshow = useSelector(getSlideshowData);
  const outbrainWidgetID = useSelector(getOutbrainWidgetID);
  const outbrainFiniteScrollWidgetID = useSelector(getOutbrainFiniteScrollWidgetID);
  const breadCrumbs = useSelector(getBreadCrumbs);
  const pageDescription = useSelector(getMetadataDescription);
  const seoTitle = useSelector(getSeoTitle) || title;
  const pageImage = useSelector(getImageCover);
  const createdAt = useSelector(getCreatedAt);
  const createdAtISO = useSelector(getCreatedAtISO);
  const updatedAt = useSelector(getUpdatedAt);
  const updatedAtISO = useSelector(getUpdatedAtISO);
  const showUpdatedAt = useSelector(getShowUpdatedAt);
  const noIndex = useSelector(getNoIndex);
  const ogDescription = useSelector(getOgDescription);
  const ogTitle = useSelector(getOgTitle);
  const ogImage = useSelector(getOgImage);
  const twitterDescription = useSelector(getTwitterDescription);
  const twitterTitle = useSelector(getTwitterTitle);
  const twitterImage = useSelector(getTwitterImage);
  const seoDescription = useSelector(getSeoDescription);
  const disclaimerText = useSelector(getDisclaimerText);
  const shouldDisplayDisclaimerAtBottom = useSelector(getShouldDisplayDisclaimerAtBottom);
  const articleSection = breadCrumbs ? breadCrumbs.categoryDisplayName : '';
  const tags = useSelector(getTags);
  const shouldAddMMPlayerPlaceholder = useSelector(getShouldAddMMPlayerPlaceholder);
  const finiteScrollArticles = useSelector(getFiniteScrollArticles);
  const articlesUrls = useSelector(getFiniteScrollArticlesURLs);
  const loadingArticles = useSelector(getLoadingArticles);
  const shouldSetMinHeightForTopAd = useSelector(getShouldSetMinHeightForTopAd);
  const commercialTags = useSelector(getCommercialTags);
  const experiments = useSelector(getExperiments);
  const faqData = useSelector(getFaqData);
  const articleId = useSelector(getArticleId);
  const shouldShowHeroImage = useSelector(getShouldShowHeroImage);
  const ownerUsername = useSelector(getOwnerUsername);
  const resourceID = useSelector(getResourceID);
  const isMobileViewer = useSelector(getIsMobileViewer);
  const [mainArticleURL, setMainArticleURL] = React.useState('');

  const postPageData: PostPageDataType = useMemo(() => ({
    slideshowData: slideshow,
    commercialTags,
    disclaimerText,
    showUpdatedAt,
  }), [slideshow, commercialTags, disclaimerText, showUpdatedAt]);

  React.useEffect(() => {
    const sessionId = getCookieByName('mm-session-id');
    // @ts-ignore TS2339
    window.OB_extIdSecondary = window.OB_extIdSecondary || [];
    // @ts-ignore TS2339
    window.OB_extIdSecondary = articleId;
    // @ts-ignore TS2339
    window.OB_extId = window.OB_extId || [];
    // @ts-ignore TS2339
    window.OB_extId = experiments.join() || 'none_experiment';
    // @ts-ignore TS2339
    window.OB_pubImpId = sessionId;
  }, [experiments, articleId]);

  React.useEffect(() => {
    if (!mainArticleURL && window) {
      setMainArticleURL(window.location.href);
    }
  }, [mainArticleURL]);
  return (
    <React.Fragment>
      <PostMetaTags {...{
        pageDescription,
        seoTitle,
        pageImage,
        createdAtISO,
        updatedAtISO,
        noIndex,
        ogDescription,
        ogTitle,
        ogImage,
        twitterDescription,
        twitterTitle,
        twitterImage,
        seoDescription,
        articleSection,
        authors,
        prevSlideshowUrl: slideshow && slideshow.prevLink,
        nextSlideshowUrl: slideshow && slideshow.nextLink,
        tags,
        breadCrumbs,
        faqData,
      }}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainArticleContent">
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight={shouldSetMinHeightForTopAd} />
            {shouldAddMMPlayerPlaceholder ? <MMPlayerPlaceHolder renderForMobile isMobileViewer={isMobileViewer} /> : null}
            <FiniteScroll
              articles={finiteScrollArticles}
              articleComponent={BaliArticleComponent}
              articlesUrls={articlesUrls}
              loadingArticles={loadingArticles}
              postPageData={postPageData}
              by={by}
              siteName={siteName}
              templateName={templateName}
              outbrainWidgetID={outbrainFiniteScrollWidgetID}
              openWebId={null}
              shouldDisplayDisclaimerAtBottom={shouldDisplayDisclaimerAtBottom}
              experiment={experiments.join()}
              isMobileViewer={isMobileViewer}
            >
              <BaliArticleComponent
                {...{
                  breadCrumbs,
                  title,
                  intro,
                  by,
                  createdAt,
                  createdAtISO,
                  updatedAt,
                  updatedAtISO,
                  showUpdatedAt,
                  authors,
                  shareConfig,
                  canonical,
                  cover,
                  siteName,
                  templateName,
                  sponsor,
                  slideshow,
                  body,
                  sideBar1Id,
                  sideBar2Id,
                  sideBar1AdOnSuccess,
                  sideBar2AdOnSuccess,
                  showRecommendationsAdvertisement,
                  outbrainWidgetID,
                  openWebId: null,
                  md5hashPostCanonicalUrl: null,
                  tags,
                  mainArticleURL,
                  disclaimerText,
                  shouldDisplayDisclaimerAtBottom,
                  addMMPlayerPlaceholder: shouldAddMMPlayerPlaceholder,
                  experiments,
                  commercialTags,
                  shouldShowHeroImage,
                  ownerUsername,
                  resourceID,
                  isMobileViewer,
                }}
              />
            </FiniteScroll>
            <Ad id={oneOnOneId} onSuccess={oneOnOneOnSuccess} />
          </NavigationLayout>
          <Ad id={stickyBottomId} onSuccess={stickyBottomOnSuccess} isSticky />
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};
